import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../styles/legal.css";
import logo from "../assets/PetfamLogo.png";
import { X } from "lucide-react";

export default function Terminos() {
  const navigate = useNavigate();
  const location = useLocation();

  // =========================================================
  // 🔀 Esta página se abre desde dos lugares:
  // - Desde el HOME (sin estado): se ve igual que siempre, con el botón
  //   "Suscribirse", y la X lleva al inicio.
  // - Desde la INTERFAZ (panel "Edita tu perfil" → Nosotros), que la abre
  //   con state { from: "interfaz" }: se oculta "Suscribirse" y la X (y el
  //   logo) vuelven a la interfaz con el panel de perfil abierto en
  //   "Nosotros".
  // =========================================================
  const fromInterfaz = location.state?.from === "interfaz";

  const handleClose = () => {
    if (fromInterfaz) {
      navigate("/interfaz", { replace: true, state: { reopenProfilePanel: "about" } });
    } else {
      navigate("/");
    }
  };

  // Sistema de animaciones de entrada
  const t = location.state?.transition;
  const screenUp = t === "screen" || t === "screen-up" || t === "up";
  const screenDown = t === "screen-down" || t === "down";

  const [enterClass, setEnterClass] = useState(
    screenDown ? "is-entering-down" : screenUp ? "is-entering" : ""
  );

  useEffect(() => {
    if (screenUp || screenDown) {
      const id = setTimeout(() => setEnterClass(""), 420);
      return () => clearTimeout(id);
    }
  }, [screenUp, screenDown]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const lastUpdated = "7 de noviembre de 2025";

  return (
    <div className={`legal-shell ${enterClass}`}>
      {/* ✕ para volver (al inicio o a la interfaz, según desde dónde se abrió).
          Los estilos van aquí y no en legal.css para que el botón se vea sí o
          sí: antes quedaba invisible o tapado por el header. */}
      <button
        className="close-btn legal-close-fixed"
        onClick={handleClose}
        aria-label={fromInterfaz ? "Volver a tu perfil" : "Volver al inicio"}
        title={fromInterfaz ? "Volver a tu perfil" : "Volver al inicio"}
        style={{
          position: "fixed",
          top: "max(16px, env(safe-area-inset-top))",
          right: "16px",
          zIndex: 9999,
          width: 44,
          height: 44,
          borderRadius: "50%",
          border: "none",
          background: "#FFFFFF",
          color: "#0B2F3E",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
          boxShadow: "0 8px 22px rgba(11,47,62,.18)",
          padding: 0,
        }}
      >
        <X size={20} strokeWidth={2.2} />
      </button>

      {/* header */}
      <header className="legal-header">
        <div className="legal-header-inner">
          {fromInterfaz ? (
            <button
              type="button"
              className="legal-logo"
              onClick={handleClose}
              aria-label="Volver a tu perfil"
              style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
            >
              <img src={logo} alt="Petfam logo" />
            </button>
          ) : (
            <Link to="/" className="legal-logo" aria-label="Ir al inicio">
              <img src={logo} alt="Petfam logo" />
            </Link>
          )}

          {/* "Suscribirse" solo tiene sentido en el Home, no para quien ya es usuario */}
          {!fromInterfaz && (
            <Link to="/crear-cuenta" className="legal-cta">
              Suscribirse
            </Link>
          )}
        </div>
      </header>

      {/* contenido */}
      <main className="legal-main" role="main">
        <section className="legal-hero">
          <h1 className="legal-title">Términos y condiciones de uso</h1>
          <p className="legal-updated">Última actualización: {lastUpdated}</p>

          <div className="legal-content">
            <p>
              Bienvenido a <strong>PETFAM</strong>, una plataforma digital operada por
              <strong> Vetia Group S.A.S.</strong>, identificada con NIT 901986820, con
              domicilio en Colombia, cuyo objetivo es facilitar la conexión entre
              usuarios y profesionales veterinarios o etólogos a través de consultas
              virtuales, acceso a historia clínica digital y calendario de actividades
              para el cuidado de las mascotas.
            </p>

            <h3>1. Aceptación de los términos</h3>
            <p>
              Al acceder, registrarse o utilizar PETFAM (app o sitio web), el usuario
              declara haber leído, comprendido y aceptado estos Términos y Condiciones.
              Si no está de acuerdo, deberá abstenerse de usar la plataforma.
            </p>

            <h3>2. Registro y uso de la cuenta</h3>
            <p>
              El usuario debe crear una cuenta personal para acceder a los servicios. La
              información suministrada debe ser veraz, completa y actualizada. El usuario
              es responsable de mantener la confidencialidad de su cuenta y contraseña, y
              de toda actividad que ocurra bajo su cuenta.
            </p>

            <h3>3. Naturaleza del servicio</h3>
            <p>
              PETFAM ofrece acceso a consultas virtuales con veterinarios y etólogos,
              historia clínica de la mascota y un calendario de recordatorios de salud y
              bienestar. Los servicios adicionales como tienda virtual, atención a
              domicilio o programas de puntos podrán incorporarse más adelante y estarán
              sujetos a condiciones adicionales.
            </p>

            <h3>4. Suscripción y pagos</h3>
            <p>
              El acceso a los servicios de PETFAM requiere una suscripción mensual
              recurrente, cuyo valor se cobra automáticamente mediante la pasarela de
              pagos asociada a la cuenta del usuario. El usuario autoriza el cobro
              periódico mientras mantenga activa su suscripción. El valor de la
              suscripción podrá ser modificado con previo aviso de al menos 15 días.
            </p>

            <h3>5. Uso adecuado del servicio</h3>
            <p>
              El usuario se compromete a utilizar PETFAM únicamente para fines lícitos y
              personales. Queda prohibido compartir cuentas, realizar grabaciones no
              autorizadas de las consultas, o cualquier acción que vulnere derechos de
              privacidad o propiedad intelectual.
            </p>

            <h3>6. Limitación de responsabilidad</h3>
            <p>
              PETFAM y Vetia Group S.A.S. actúan como intermediarios tecnológicos entre
              el usuario y los profesionales de salud animal. Las recomendaciones
              emitidas durante las consultas son responsabilidad del profesional que las
              emite. PETFAM no asume responsabilidad por decisiones tomadas por el
              usuario con base en dichas recomendaciones ni por fallas derivadas del
              servicio de internet o dispositivos del usuario.
            </p>

            <h3>7. Propiedad intelectual</h3>
            <p>
              Todo el contenido, marca, diseño y software de PETFAM son propiedad
              exclusiva de Vetia Group S.A.S. Queda prohibida su copia, distribución o
              uso sin autorización previa y por escrito.
            </p>

            <h3>8. Terminación y suspensión</h3>
            <p>
              PETFAM podrá suspender o cancelar una cuenta si detecta uso indebido,
              fraude o incumplimiento de estos términos. El usuario podrá cancelar su
              suscripción en cualquier momento, con efecto a partir del siguiente ciclo
              de facturación.
            </p>

            <h3>9. Contacto</h3>
            <p>
              Para soporte o consultas, puede escribir a:{" "}
              <a href="mailto:soporte@petfam.club" className="legal-link">
                soporte@petfam.club
              </a>{" "}
              o a la línea móvil{" "}
              <a href="tel:+573168756765" className="legal-link">
                +57 316 875 6765
              </a>
            </p>

            <div className="legal-footnote">
              PETFAM podrá actualizar estos términos cuando sea necesario. Revise
              periódicamente esta página para conocer la versión vigente.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}